<template>
  <PageLoading :loading="loading">
    <!-- 错误页面 -->
    <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />

    <!-- 正常页面 -->
    <div v-else class="student-list-page">
      <!-- 新增学生按钮 -->
      <div class="add-student-section">
        <van-button class="add-student-btn" icon="plus" block @click="showAddStudent = true">
          新增学生
        </van-button>
      </div>

      <!-- 学生卡片列表 -->
      <!-- 空状态 -->
      <div v-if="students.length === 0" class="empty-state">
        <van-empty description="暂无学生资料" />
      </div>
      <!-- 学生列表 -->
      <div v-else>
        <div class="student-cards">
          <div class="student-card" v-for="student in students" :key="student.id">
            <div class="student-info">
              <van-image round fit="cover" class="avatar"
                :src="Object.keys(student.face_image_arr || {}).length > 0 ? student.face_image_arr.face_image_url : userDefaultImage" />
              <div class="info">
                <div class="name-row">
                  <span class="name">{{ student.name }}</span>
                  <div class="status-tags">
                    <van-tag class="status-tag milk-tag" color="#DCFCE7" text-color="#16A34A" round
                      v-if="student.milk_state === '已订奶'">
                      已订奶
                    </van-tag>
                    <van-tag class="status-tag face-tag" color="#F3F4F6" text-color="#4B5563" round
                      v-if="student.milk_state === '未订奶'">
                      未订奶
                    </van-tag>
                    <van-tag class="status-tag face-tag" color="#DBEAFE" text-color="#2563EB" round
                      v-if="Object.keys(student.face_image_arr || {}).length > 0">
                      已录脸
                    </van-tag>
                    <van-tag class="status-tag face-tag" color="#FEF9C3" text-color="#CA8A04" round
                      v-if="Object.keys(student.face_image_arr || {}).length == 0">
                      未录脸
                    </van-tag>
                  </div>
                </div>
                <div class="info-bottom">
                  <div class="info-left">
                    <div class="school">{{ student.school_name }}</div>
                    <div class="school">{{ student.grade_name }}</div>
                    <div class="class">{{ student.class_name }}</div>
                  </div>
                  <div class="info-actions">
                    <van-button class="edit-btn" size="small" type="primary" plain @click="handleEditClick(student)">
                      修改
                    </van-button>
                    <van-button class="unbind-btn" size="small" type="primary" plain @click="handleUnbindClick(student)">
                      解绑
                    </van-button>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-actions">
              <van-button class="action-btn book-btn" color="#8B0000" block @click="handleBookClick(student)">
                每日限购
              </van-button>
              <van-button class="action-btn face-btn" type="primary" size="small"
                v-if="Object.keys(student.face_image_arr || {}).length == 0" @click="handleFaceClick(student)">
                录入人脸
              </van-button>
              <van-button class="action-btn auth-btn" type="success" size="small"
                v-if="Object.keys(student.face_image_arr || {}).length == 0 && student.face_record_state !== 'pending'"
                @click="handleAuthClick(student)">
                授权学校录脸
              </van-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 新增学生弹框 -->
      <AddStudentModal v-model:show="showAddStudent" @submit="handleAddStudent" />
      
      <!-- 编辑学生弹框 -->
      <AddStudentModal 
        v-model:show="showEditStudent" 
        mode="edit" 
        :edit-data="currentEditStudent" 
        @submit="handleEditStudent" 
      />
      
      <!-- 每日限购弹框 -->
      <DailyLimitModal v-model:show="showDailyLimit" :student-data="currentStudent" @submit="handleDailyLimitSubmit" />
    </div>
  </PageLoading>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { setWxTitle } from '@/utils/wx'
import AddStudentModal from '@/components/AddStudentModal.vue'
import DailyLimitModal from '@/components/DailyLimitModal.vue'
import { post } from '@/utils/request'
import { WECHAT_APPID } from '../../config/env'
import { Dialog, Toast } from 'vant'

// 加载状态
const loading = ref(true)
// 用户默认头像
const userDefaultImage = ref(localStorage.getItem('user_default_image'))
const userInfo = ref({})

// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

const showAddStudent = ref(false)
const showEditStudent = ref(false)
const showDailyLimit = ref(false)
const currentEditStudent = ref(null)
const currentStudent = ref(null)

const students = ref([])

// 处理错误
const handleError = (title, message) => {
  hasError.value = true
  errorTitle.value = title
  errorMessage.value = message
  loading.value = false
}

onMounted(() => {
  setWxTitle('学生资料')
  initPage()
})

// 页面初始化
const initPage = async () => {
  try {
    // 这里可以调用接口获取学生列表数据
    const res = await post('/api/index/info', {
      partner_id: parseInt(localStorage.getItem('userId')),
      page_type: '学生资料',
      app_id: WECHAT_APPID,
    })
    if (res.result && res.result.state === 'ok') {
      userInfo.value = res.result.partner_info
      students.value = res.result.partner_info.student_ids
    } else {
      handleError('加载失败', res.result?.message || '接口异常')
    }
  } catch (error) {
    console.error('页面初始化失败:', error)
    handleError('加载失败', '页面加载失败，请重试')
  } finally {
    loading.value = false
  }
}

// 处理新增学生
const handleAddStudent = (studentData) => {
  console.log('新增学生数据:', studentData)
  // 这里可以调用API添加学生
  // 添加成功后可以刷新学生列表
  initPage() // 刷新学生列表
}

const handleBookClick = (student) => {
  console.log('点击每日限购:', student)
  currentStudent.value = student
  showDailyLimit.value = true
}

const handleDailyLimitSubmit = () => {
  console.log('每日限购提交:', currentStudent.value)
  initPage() // 刷新学生列表
}

const handleFaceClick = (student) => {
  console.log('点击录入人脸:', student)
  window.location.href = '/face-recognition?partner_id=' + student.id
}

const handleAuthClick = (student) => {
  console.log('点击授权学校录脸:', student)
  Dialog.confirm({
    title: '确认',
    message: '是否授权班主任或学校业务负责人进行录脸？',
    closeOnClickOverlay: false,
    beforeClose: async (action) => {
      if (action === 'confirm') {
        try {
          const res = await post('/api/add/face/authorization', {
            partner_id: parseInt(localStorage.getItem('userId')),
            student_id: student.id,
          })
          if (res.result && res.result.state === 'ok') {
            Toast.success('授权成功')
            initPage()
            return true
          } else {
            Dialog.alert({
              title: '授权失败',
              message: res.result?.message || '接口异常'
            })
            return false
          }
        } catch (error) {
          console.error('授权失败:', error)
          Dialog.alert({
            title: '授权失败',
            message: '网络错误，请重试'
          })
          return false
        }
      }
      return true
    }
  })
}

// 处理编辑按钮点击
const handleEditClick = (student) => {
  console.log('点击编辑学生:', student)
  // 构造编辑数据，确保字段名称与AddStudentModal组件期望的一致
  // 注意：这里需要根据实际的学生数据结构来调整字段映射
  currentEditStudent.value = {
    id: student.id,
    name: student.name,
    // 如果学生数据中没有这些ID字段，可能需要通过其他方式获取
    school_id: student.school_id || null,
    grade_id: student.grade_id || null,
    class_id: student.class_id || null,
    parent_phone: userInfo.value.mobile || '',
    school_quota_num: student.school_quota_num || '',
    quota_num: student.quota_num || ''
  }
  
  showEditStudent.value = true
}

// 处理编辑学生提交
const handleEditStudent = (studentData) => {
  console.log('编辑学生数据:', studentData)
  // 编辑成功后刷新学生列表
  initPage() // 刷新学生列表
  showEditStudent.value = false
  currentEditStudent.value = null
}

// 处理解绑按钮点击
const handleUnbindClick = (student) => {
  console.log('点击解绑学生:', student)
  Dialog.confirm({
    title: '确认解绑',
    message: '确认解绑该学生？',
    closeOnClickOverlay: false,
    beforeClose: async (action) => {
      if (action === 'confirm') {
        try {
          const res = await post('/api/unbind/student', {
            partner_id: parseInt(localStorage.getItem('userId')),
            student_id: student.id,
          })
          if (res.result && res.result.state === 'ok') {
            Toast.success('解绑成功')
            initPage() // 刷新页面，解绑后的学生将不再显示
            return true
          } else {
            Dialog.alert({
              title: '解绑失败',
              message: res.result?.message || '接口异常'
            })
            return false
          }
        } catch (error) {
          console.error('解绑失败:', error)
          Dialog.alert({
            title: '解绑失败',
            message: '网络错误，请重试'
          })
          return false
        }
      }
      return true
    }
  })
}
</script>

<style scoped>
.student-list-page {
  padding: 13px;
}

/* 新增学生按钮 */
.add-student-section {
  margin-bottom: 16px;
}

.add-student-btn {
  background: #374151;
  color: #fff;
  font-size: 16px;
  border-radius: 4px;
  border: none;
  height: 48px;
}

.add-student-btn:active {
  background: #374151;
}

/* 学生卡片 */
.student-cards {
  padding-bottom: 80px;
}

.empty-state {
  padding: 60px 0;
  text-align: center;
}

.student-card {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 16px;
  padding: 14px;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.student-info {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.avatar {
  width: 48px;
  height: 48px;
  margin-right: 16px;
}

.info {
  flex: 1;
}

.name-row {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
}

.name {
  font-size: 16px;
  font-weight: 600;
  color: #1F2937;
}

.status-tags {
  display: flex;
  gap: 8px;
  margin-left: 12px;
}

.status-tag {
  font-size: 12px;
  padding: 4px 8px;
}

.info-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.school {
  color: #6B7280;
  font-size: 14px;
  margin-bottom: 2px;
}

.class {
  color: #9CA3AF;
  font-size: 14px;
}

.info-actions {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-left: 8px;
}

.edit-btn,
.unbind-btn {
  flex: 1;
  height: 32px;
  font-size: 12px;
  border-radius: 22px;
  padding: 4px 8px;
  border: 1px solid #C9C6C6;
  color: #000000;
}

/* .edit-btn {
  border: 1px solid #4F46E5;
  color: #4F46E5;
}

.unbind-btn {
  border: 1px solid #EF4444;
  color: #EF4444;
} */

/* 卡片操作按钮 */
.card-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  flex: 1;
  height: 36px;
  font-size: 14px;
  border-radius: 4px;
  padding: 8px;
}

.book-btn {
  background: var(--van-primary-color);
  border: none;
}

.book-btn:active {
  background: var(--van-primary-color);
}

.face-btn {
  background: #3B82F6;
  border: none;
}

.face-btn:active {
  background: #2563EB;
}

.auth-btn {
  background: #10B981;
  border: none;
}

.auth-btn:active {
  background: #059669;
}
</style>